import {
  normalizeNcrmLeadEntryStatus,
  normalizeNcrmLeadString,
} from "@/Utile/ncrmLeads.mjs";

export const DEFAULT_VIEW = "board";
export const LEAD_BOARD_VIEW_ITEMS = Object.freeze([{ value: "board" }, { value: "table" }]);
export const LEAD_BOARD_PAGE_KEY_FALLBACK = "/ncrm/leads/boards";

export const parsePositiveId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

export const toUid = () => `bulk-row-${Math.random().toString(36).slice(2, 9)}-${Date.now()}`;

export const LEAD_CARD_DEFAULT_LAYOUT = Object.freeze({
  company_fields: Object.freeze(["owner", "status", "next_activity", "entity_type", "custom_auto"]),
  person_fields: Object.freeze(["owner", "status", "next_activity", "entity_type", "custom_auto"]),
  density: "comfortable",
});

export const LEAD_DENSITY_ORDER = Object.freeze(["comfortable", "standard", "compact", "micro"]);
export const LEAD_STAGE_VISIBLE_BATCH = 20;
export const LEAD_TABLE_SORT_KEYS = Object.freeze(["entity", "stage_name", "status", "owner_name", "updated_at"]);

export const createEmptyOwnerClaimDialog = () => ({
  open: false,
  loading: false,
  entryId: null,
  entryName: "",
  currentOwnerLabel: "",
});

export const ensureArray = (value) => (Array.isArray(value) ? value : []);

export const normalizeLeadBoardDensity = (value, fallback = LEAD_CARD_DEFAULT_LAYOUT.density) => {
  const normalized = normalizeNcrmLeadString(value).toLowerCase();
  if (LEAD_DENSITY_ORDER.includes(normalized)) return normalized;
  return LEAD_DENSITY_ORDER.includes(fallback) ? fallback : LEAD_CARD_DEFAULT_LAYOUT.density;
};

export const dedupeFieldKeys = (value) => {
  const source = ensureArray(value);
  const seen = new Set();
  const result = [];
  for (const entry of source) {
    const key = normalizeNcrmLeadString(entry);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(key);
  }
  return result;
};

export const dedupeLeadFilterFieldItems = (items) => {
  const source = ensureArray(items);
  const seenStandardKeys = new Set();
  const seenCustomLabels = new Set();
  const result = [];
  for (const entry of source) {
    const key = normalizeNcrmLeadString(entry?.key);
    const label = normalizeNcrmLeadString(entry?.label) || key;
    const sourceType = normalizeNcrmLeadString(entry?.source).toLowerCase() || "standard";
    if (!key) continue;
    if (sourceType === "custom") {
      const labelSignature = normalizeNcrmLeadString(label).toLowerCase();
      if (labelSignature && seenCustomLabels.has(labelSignature)) continue;
      if (labelSignature) seenCustomLabels.add(labelSignature);
    } else {
      if (seenStandardKeys.has(key)) continue;
      seenStandardKeys.add(key);
    }
    result.push({
      key,
      label,
      source: sourceType,
    });
  }
  return result;
};

export const normalizeLeadTableSortKey = (value) => {
  const normalized = normalizeNcrmLeadString(value).toLowerCase();
  return LEAD_TABLE_SORT_KEYS.includes(normalized) ? normalized : null;
};

export const normalizeLeadTableSortOrder = (value) => {
  const normalized = normalizeNcrmLeadString(value).toLowerCase();
  return normalized === "asc" || normalized === "desc" ? normalized : null;
};

export const resolveLeadBoardOwnerLabel = (entry = {}) => {
  const label = normalizeNcrmLeadString(entry?.owner_name ?? entry?.ownerName);
  if (label) return label;
  const id = parsePositiveId(entry?.owner_user_id ?? entry?.ownerUserId);
  return id ? `#${id}` : "-";
};

export const shortenLeadBoardInlineValue = (value, maxLength = 24) => {
  const normalized = normalizeNcrmLeadString(value);
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(1, maxLength - 3)).trimEnd()}...`;
};

export const parseLeadBoardDateToMs = (value) => {
  const normalized = normalizeNcrmLeadString(value);
  if (!normalized) return null;
  const parsed = new Date(normalized.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getTime();
};

export const isLeadBoardDueDateOverdue = (value) => {
  const dueAtMs = parseLeadBoardDateToMs(value);
  if (dueAtMs === null) return false;
  return dueAtMs < Date.now();
};

export const buildLeadBoardActivityTypeShortLabel = (activityType, { t, te } = {}) => {
  const normalized = normalizeNcrmLeadString(activityType).toLowerCase();
  if (!normalized) return "";
  const i18nKey = `ncrm.activity.types.${normalized}`;
  if (typeof te === "function" && te(i18nKey)) {
    return normalizeNcrmLeadString(t(i18nKey));
  }
  const fallbackMap = {
    todo: "To do",
    call: "Call",
    email: "Email",
    meeting: "Meeting",
    note: "Note",
  };
  return fallbackMap[normalized] || normalized;
};

export const resolveLeadBoardStatusLabel = (status, t) => {
  const normalized = normalizeNcrmLeadEntryStatus(status, "open") || "open";
  return t(`ncrm.leads.statuses.${normalized}`);
};

export const resolveLeadBoardStatusColor = (status) => {
  const normalized = normalizeNcrmLeadEntryStatus(status, "open") || "open";
  if (normalized === "open") return "primary";
  if (normalized === "converted") return "success";
  if (normalized === "failed") return "error";
  if (normalized === "disqualified") return "warning";
  if (normalized === "no_response") return "neutral";
  return "primary";
};

export const resolveLeadBoardEntityTypeLabel = (type, t) => {
  const normalized = normalizeNcrmLeadString(type).toLowerCase();
  if (normalized !== "company" && normalized !== "person") return "-";
  return t(`ncrm.leads.entityTypes.${normalized}`);
};

export const buildLeadBoardEntryFieldLabel = (fieldKey, t) => {
  const key = normalizeNcrmLeadString(fieldKey).toLowerCase();
  const map = {
    owner: t("ncrm.leads.cardLayout.labels.owner"),
    status: t("ncrm.leads.cardLayout.labels.status"),
    next_followup: t("ncrm.leads.cardLayout.labels.nextFollowup"),
    next_activity: t("ncrm.leads.cardLayout.labels.nextActivity"),
    entity_type: t("ncrm.leads.cardLayout.labels.entityType"),
    source: t("ncrm.leads.cardLayout.labels.source"),
    business_category: t("ncrm.leads.cardLayout.labels.businessCategory"),
    industry: t("ncrm.leads.cardLayout.labels.industry"),
    email_primary: t("ncrm.leads.cardLayout.labels.emailPrimary"),
    phone_primary: t("ncrm.leads.cardLayout.labels.phonePrimary"),
    website: t("ncrm.leads.cardLayout.labels.website"),
    position: t("ncrm.leads.cardLayout.labels.position"),
    department: t("ncrm.leads.cardLayout.labels.department"),
    linked_company: t("ncrm.leads.cardLayout.labels.linkedCompany"),
    updated_at: t("ncrm.leads.cardLayout.labels.updatedAt"),
    custom_auto: t("ncrm.leads.cardLayout.labels.customAuto"),
  };
  return map[key] || key;
};

export const buildLeadBoardNextActivityInlineLabel = (entry = {}, { formatDateTime, t, te } = {}) => {
  const dueLabel = typeof formatDateTime === "function" ? formatDateTime(entry?.next_activity_due_at) : "";
  const dueSafe = dueLabel && dueLabel !== "-" ? dueLabel : "";
  const titleSafe = shortenLeadBoardInlineValue(entry?.next_activity_title, 22);
  const typeSafe = buildLeadBoardActivityTypeShortLabel(entry?.next_activity_type, { t, te });
  const namePart = titleSafe || typeSafe;
  if (namePart && dueSafe) return `${namePart} • ${dueSafe}`;
  if (namePart) return namePart;
  if (dueSafe) return dueSafe;
  return "-";
};

// Coloanele view-ului tabel al board-ului de lead-uri (extras din NcrmLeadBoard.vue,
// fisier peste pragul de dimensiune). `last_call` este celula „Ultimul apel".
export const buildLeadBoardTableHeaders = (t) => [
  { title: t("ncrm.leads.board.tableHeaders.entity"), key: "entity" },
  { title: t("ncrm.activity.lastCall.header"), key: "last_call", sortable: false },
  { title: t("ncrm.leads.board.tableHeaders.stage"), key: "stage_name", sortable: false },
  { title: t("ncrm.leads.board.tableHeaders.status"), key: "status", sortable: false },
  { title: t("ncrm.leads.board.tableHeaders.owner"), key: "owner_name", sortable: false },
  { title: t("ncrm.leads.board.tableHeaders.updatedAt"), key: "updated_at" },
  { title: "", key: "row_nav", sortable: false, width: "48px" },
];
